import { jsPDF } from 'jspdf';
import { autoTable } from 'jspdf-autotable';
import api from './axiosInstance.js';
import abroadifyLogo from '../assets/img/logo-large.png';

const getPageWidth = (doc) => doc.internal.pageSize.getWidth();
const getPageHeight = (doc) => doc.internal.pageSize.getHeight();

const loadImage = (src) =>
    new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
    });

const addPdfHeader = async (doc) => {
    try {
        const img = await loadImage(abroadifyLogo);
        const maxWidth = 58;
        const maxHeight = 20;
        const imgRatio = img.width / img.height;
        let renderWidth = maxWidth;
        let renderHeight = renderWidth / imgRatio;
        if (renderHeight > maxHeight) {
            renderHeight = maxHeight;
            renderWidth = renderHeight * imgRatio;
        }
        const x = (getPageWidth(doc) - renderWidth) / 2;
        doc.addImage(abroadifyLogo, 'PNG', x, 10, renderWidth, renderHeight);
    } catch {
        doc.setFontSize(18);
        doc.text('ABROADIFY', getPageWidth(doc) / 2, 20, { align: 'center' });
    }
    doc.setDrawColor(192, 217, 238);
    doc.line(14, 32, getPageWidth(doc) - 14, 32);
};

const addPdfFooter = (doc) => {
    const issuingDate = new Date().toLocaleDateString();
    doc.setDrawColor(192, 217, 238);
    const pageHeight = getPageHeight(doc);
    const footerY = pageHeight - 10;
    const lineY = pageHeight - 17;
    const rightX = getPageWidth(doc) - 14;
    doc.line(14, lineY, rightX, lineY);
    doc.setFontSize(9);
    doc.text(`Issuing Date: ${issuingDate}`, 14, footerY);
    doc.textWithLink('app.abroadify.me', rightX, footerY, {
        align: 'right',
        url: 'https://app.abroadify.me',
    });
    doc.setTextColor(0, 0, 0);
};

const addHeaderAndFooterToAllPages = async (doc) => {
    const totalPages = doc.getNumberOfPages();
    for (let page = 1; page <= totalPages; page += 1) {
        doc.setPage(page);
        await addPdfHeader(doc);
        addPdfFooter(doc);
    }
};

const formatValue = (value) => {
    if (value === null || value === undefined || value === '') return '—';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (Array.isArray(value)) return value.length ? value.join(', ') : '—';
    return String(value);
};

const addSectionTable = (doc, title, rows, startY) => {
    const filteredRows = rows.filter(([, value]) => {
        if (value === null || value === undefined) return false;
        if (Array.isArray(value)) return value.length > 0;
        return String(value).trim() !== '';
    });

    if (!filteredRows.length) return startY;

    if (startY > 240) {
        doc.addPage();
        startY = 42;
    }

    doc.setFontSize(13);
    doc.setTextColor(20, 20, 20);
    doc.text(title, 14, startY);
    doc.setTextColor(0, 0, 0);

    autoTable(doc, {
        startY: startY + 4,
        head: [['Field', 'Value']],
        body: filteredRows.map(([label, value]) => [label, formatValue(value)]),
        theme: 'grid',
        styles: {
            fontSize: 9,
            cellPadding: 2.5,
            overflow: 'linebreak',
            valign: 'top',
            lineColor: [192, 217, 238],
            lineWidth: 0.3,
            textColor: [45, 45, 45],
        },
        headStyles: {
            fontSize: 10,
            fillColor: [240, 248, 255],
            textColor: [26, 26, 26],
            lineColor: [192, 217, 238],
            lineWidth: 0.3,
            fontStyle: 'bold',
        },
        bodyStyles: {
            lineColor: [192, 217, 238],
            lineWidth: 0.3,
        },
        alternateRowStyles: {
            fillColor: [255, 255, 255],
        },
        columnStyles: {
            0: { cellWidth: 55 },
            1: { cellWidth: 127 },
        },
        margin: { top: 42, left: 14, right: 14, bottom: 20 },
    });

    return doc.lastAutoTable.finalY + 10;
};

const formatWorkingHours = (workingHours = []) => {
    if (!Array.isArray(workingHours) || !workingHours.length) return '';
    return workingHours
        .map((wh, i) => {
            const parts = [
                wh.fromDay && wh.toDay ? `${wh.fromDay} – ${wh.toDay}` : wh.fromDay || wh.toDay || '',
                wh.fromTime && wh.toTime ? `${wh.fromTime} – ${wh.toTime}` : wh.fromTime || wh.toTime || '',
            ].filter(Boolean);
            return parts.length ? `#${i + 1}: ${parts.join(', ')}` : null;
        })
        .filter(Boolean)
        .join('\n');
};

const formatUniversities = (universities = []) => {
    if (!Array.isArray(universities) || !universities.length) return '';
    return universities
        .map((u, i) => {
            const parts = [
                u.name ? `Name: ${u.name}` : '',
                u.address ? `Address: ${u.address}` : '',
                u.telephone ? `Tel: ${u.telephone}` : '',
                u.url ? `URL: ${u.url}` : '',
                u.classification ? `Classification: ${u.classification}` : '',
                u.studentsNumber != null && u.studentsNumber !== '' ? `Students: ${u.studentsNumber}` : '',
            ].filter(Boolean);
            return parts.length ? `#${i + 1}: ${parts.join(' | ')}` : null;
        })
        .filter(Boolean)
        .join('\n');
};

const formatCountry = (country) => {
    if (!country || typeof country !== 'object') return '';
    return [country.name, country.iso].filter(Boolean).join(' / ');
};

const addCompaniesTable = (doc, companies, startY) => {
    if (!Array.isArray(companies) || !companies.length) return startY;

    if (startY > 240) {
        doc.addPage();
        startY = 42;
    }

    doc.setFontSize(13);
    doc.setTextColor(20, 20, 20);
    doc.text('Companies', 14, startY);
    doc.setTextColor(0, 0, 0);

    const rows = companies.map((company) => {
        const url = company.url
            ? (company.url.startsWith('http') ? company.url : `https://${company.url}`)
            : '';
        return [company.title || '—', url];
    });

    autoTable(doc, {
        startY: startY + 4,
        head: [['Company', 'Website']],
        body: rows,
        theme: 'grid',
        styles: {
            fontSize: 9,
            cellPadding: 2.5,
            overflow: 'linebreak',
            valign: 'top',
            lineColor: [192, 217, 238],
            lineWidth: 0.3,
            textColor: [45, 45, 45],
        },
        headStyles: {
            fontSize: 10,
            fillColor: [240, 248, 255],
            textColor: [26, 26, 26],
            lineColor: [192, 217, 238],
            lineWidth: 0.3,
            fontStyle: 'bold',
        },
        bodyStyles: {
            lineColor: [192, 217, 238],
            lineWidth: 0.3,
        },
        alternateRowStyles: {
            fillColor: [255, 255, 255],
        },
        columnStyles: {
            0: { cellWidth: 85 },
            1: { cellWidth: 97, textColor: [0, 0, 238] },
        },
        margin: { top: 42, left: 14, right: 14, bottom: 20 },
        didDrawCell: (data) => {
            if (data.section === 'body' && data.column.index === 1) {
                const url = data.cell.raw;
                if (url) {
                    doc.link(data.cell.x, data.cell.y, data.cell.width, data.cell.height, { url });
                }
            }
        },
    });

    return doc.lastAutoTable.finalY + 10;
};

export const exportCityPdf = async (id) => {
    const [cityResponse, companiesResponse] = await Promise.all([
        api.get(`/api/cities/${id}`),
        api.get(`/api/discovery/cities/${id}/companies`).catch(() => ({ data: [] })),
    ]);
    const d = cityResponse.data || {};
    const companies = Array.isArray(companiesResponse.data) ? companiesResponse.data : [];

    const student   = Array.isArray(d.studentJobs)   ? d.studentJobs[0]   : {};
    const intern    = Array.isArray(d.internJobs)     ? d.internJobs[0]    : {};
    const oneThree  = Array.isArray(d.oneThreeJobs)   ? d.oneThreeJobs[0]  : {};
    const three     = Array.isArray(d.threeJobs)      ? d.threeJobs[0]     : {};
    const uplift    = Array.isArray(d.uplift)         ? d.uplift[0]        : {};
    const insight   = (d.cityInsight && typeof d.cityInsight === 'object') ? d.cityInsight : {};
    const unis      = Array.isArray(d.universities)   ? d.universities     : [];

    const doc = new jsPDF();
    let y = 42;

    doc.setFontSize(18);
    doc.text(d.name || 'City Export', 14, y);
    y += 8;

    doc.setFontSize(10);
    doc.text(`Exported: ${new Date().toLocaleString()}`, 14, y);
    y += 10;

    y = addSectionTable(doc, 'General', [
        ['Name',                    d.name],
        ['City ID',                 d.cityId],
        ['Continent',               d.continent],
        ['Country',                 formatCountry(d.country)],
        ['Citizens Number',         d.citizensNumber],
        ['Region Citizens Number',  d.regionCitizensNumber],
        ['Languages Spoken',        Array.isArray(d.languagesSpoken) ? d.languagesSpoken.map(l => l?.name || l).join(', ') : d.languagesSpoken],
        ['City / Region Description', d.cityRegionDescription],
        ['URL',                     d.url],
        ['Government URL',          d.govUrl],
    ], y);

    y = addSectionTable(doc, 'Office / Citizen Desk', [
        ['Address',     d.address],
        ['Phone',       d.phone],
        ['Office URL',  d.officeUrl],
        ['Working Hours', formatWorkingHours(d.workingHours)],
    ], y);

    y = addSectionTable(doc, 'Salary', [
        ['Currency',    d.currency],
        ['Legal URL',   d.legalUrl],
        ['Remarks',     d.remarks],
    ], y);

    y = addSectionTable(doc, 'Salary – Student Jobs', [
        ['Central (from – to)',    student?.centralFrom != null    ? `${student.centralFrom} – ${student.centralTo}` : ''],
        ['Production (from – to)', student?.productionFrom != null ? `${student.productionFrom} – ${student.productionTo}` : ''],
        ['IT (from – to)',         student?.itFrom != null         ? `${student.itFrom} – ${student.itTo}` : ''],
    ], y);

    y = addSectionTable(doc, 'Salary – Intern Jobs', [
        ['Central (from – to)',    intern?.internCentralFrom != null    ? `${intern.internCentralFrom} – ${intern.internCentralTo}` : ''],
        ['Production (from – to)', intern?.internProductionFrom != null ? `${intern.internProductionFrom} – ${intern.internProductionTo}` : ''],
        ['IT (from – to)',         intern?.internItFrom != null         ? `${intern.internItFrom} – ${intern.internItTo}` : ''],
    ], y);

    y = addSectionTable(doc, 'Salary – 1–3 Years Jobs', [
        ['Central (from – to)',    oneThree?.oneThreeCentralFrom != null    ? `${oneThree.oneThreeCentralFrom} – ${oneThree.oneThreeCentralTo}` : ''],
        ['Production (from – to)', oneThree?.oneThreeProductionFrom != null ? `${oneThree.oneThreeProductionFrom} – ${oneThree.oneThreeProductionTo}` : ''],
        ['IT (from – to)',         oneThree?.oneThreeItFrom != null         ? `${oneThree.oneThreeItFrom} – ${oneThree.oneThreeItTo}` : ''],
    ], y);

    y = addSectionTable(doc, 'Salary – 3+ Years Jobs', [
        ['Central (from – to)',    three?.threeCentralFrom != null    ? `${three.threeCentralFrom} – ${three.threeCentralTo}` : ''],
        ['Production (from – to)', three?.threeProductionFrom != null ? `${three.threeProductionFrom} – ${three.threeProductionTo}` : ''],
        ['IT (from – to)',         three?.threeItFrom != null         ? `${three.threeItFrom} – ${three.threeItTo}` : ''],
    ], y);

    y = addSectionTable(doc, 'Salary – Uplift', [
        ['Central',    uplift?.upliftCentral],
        ['Production', uplift?.upliftProduction],
        ['IT',         uplift?.upliftIt],
    ], y);

    y = addSectionTable(doc, 'City Insight', [
        ['Service',       insight.service],
        ['Name',          insight.name],
        ['URL',           insight.url],
        ['Provider URL',  insight.providerUrl],
        ['Description',   insight.description],
        ['Google Rating', insight.googleRating],
        ['Show Status',   insight.showStatus],
    ], y);

    y = addSectionTable(doc, 'Universities', [
        ['Universities', formatUniversities(unis)],
    ], y);

    y = addCompaniesTable(doc, companies, y);

    await addHeaderAndFooterToAllPages(doc);

    const safeFileName = (d.name || 'city')
        .replace(/[^\w\s-]/g, '')
        .replace(/\s+/g, '-');

    doc.save(`${safeFileName}-export.pdf`);
};
